import { useEffect, useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { fetchDemoSite } from '../api/demo';

/**
 * The strip across the top of a demo site: says the data is made up, how long
 * until it is put back, and which role this session is — with the others one
 * choice away. Renders nothing anywhere else.
 */
export function DemoBanner() {
  const { demo, demoLogin, enterDemo } = useAuth();
  const [resetAt, setResetAt] = useState<number | null>(demo?.resetAt ?? null);
  const [now, setNow] = useState(() => Date.now());
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!demo) return;
    const tick = window.setInterval(() => setNow(Date.now()), 15_000);
    // The reset time moves on after every reset, and when a rebuild fails.
    const poll = window.setInterval(() => {
      void fetchDemoSite().then((site) => {
        if (site) setResetAt(site.resetAt);
      });
    }, 60_000);
    return () => {
      window.clearInterval(tick);
      window.clearInterval(poll);
    };
  }, [demo]);

  if (!demo) return null;

  const minutes = resetAt ? Math.ceil((resetAt - now) / 60_000) : null;
  const when =
    minutes === null
      ? `every ${demo.resetMinutes === 60 ? 'hour' : `${demo.resetMinutes} minutes`}`
      : minutes <= 0
        ? 'any moment now'
        : minutes === 1
          ? 'in 1 minute'
          : `in ${minutes} minutes`;

  return (
    <div
      role="note"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '4px 12px',
        padding: '6px 14px',
        fontSize: 12.5,
        lineHeight: 1.35,
        textAlign: 'center',
        color: 'var(--itr-text)',
        background: 'color-mix(in srgb, var(--itr-primary) 13%, var(--itr-surface))',
        borderBottom: 'var(--itr-border-w) solid color-mix(in srgb, var(--itr-primary) 32%, transparent)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <FlaskConical size={14} style={{ flexShrink: 0, color: 'var(--itr-primary)' }} />
        <span>
          <strong>Demo</strong> — everything here is simulated. Change anything you like; it resets {when}.
        </span>
      </span>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span>Signed in as</span>
        <select
          aria-label="Switch demo role"
          value={demoLogin?.key ?? ''}
          disabled={switching}
          onChange={(e) => {
            setSwitching(true);
            // A successful switch reloads the page, so this only comes back on failure.
            void enterDemo(e.target.value).finally(() => setSwitching(false));
          }}
          style={{
            font: 'inherit',
            fontWeight: 600,
            color: 'inherit',
            padding: '2px 6px',
            borderRadius: 'var(--itr-radius-sm)',
            border: 'var(--itr-border-w) solid color-mix(in srgb, var(--itr-primary) 40%, transparent)',
            background: 'var(--itr-surface)',
          }}
        >
          {/* A session that matches none of the roles: an account someone made by hand. */}
          {!demoLogin && <option value="">another account</option>}
          {demo.logins.map((choice) => (
            <option key={choice.key} value={choice.key}>
              {choice.title}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
